/**
 * The closing "choose your Saturn" options.
 *
 * Choosing an option does exactly what a customer doing it by hand would do: it selects that
 * variant in the real variant picker, waits for the theme to finish reacting, then presses
 * the buy box's own Add to cart button. There is no cart request, no variant resolution and
 * no price lookup here — Shopify's product form remains the only thing that talks to the
 * cart, so quantity rules, availability, cart drawer behaviour and analytics all keep
 * working without being reimplemented.
 *
 * The wait is on state, never on a timer: the handler polls until the product form's own
 * `id` control reports the chosen variant and the Add to cart button is enabled. That is the
 * only reliable signal that the theme has finished processing the picker change, and it is
 * why an earlier version that submitted after a fixed pair of animation frames added
 * nothing — it fired while the form still held the previous variant.
 *
 * Note the form control named `id` shadows `HTMLFormElement.prototype.id`, so the element's
 * own identifier is only reachable via getAttribute. Reading `form.id` returns the input.
 */

/** Backstop only: how long to wait for the theme to settle before releasing the click. */
const READY_TIMEOUT = 3000;

/**
 * The buy box's Add to cart button, or null before the section has rendered.
 * Re-queried on every use: a variant change can re-render this subtree.
 *
 * @returns {HTMLButtonElement | null}
 */
function addToCartButton() {
  return document.querySelector('.product-details .add-to-cart-button');
}

/**
 * Selects a variant through the real picker.
 *
 * Matches on `data-variant-id`, which the picker puts on each radio — the radio's `value` is
 * the option label, not the id, so matching on value only works for single-option products.
 *
 * @param {string} variantId
 * @returns {boolean} whether a matching control was found
 */
function selectVariant(variantId) {
  const radio = /** @type {HTMLInputElement | null} */ (
    document.querySelector(`variant-picker input[data-variant-id="${variantId}"]`)
  );
  if (!radio) return false;

  // Already selected is a valid state, not a failure — the customer may be confirming the
  // option the page opened on.
  if (!radio.checked) radio.click();
  return true;
}

/**
 * Runs once the product form actually carries `variantId` and can be submitted.
 *
 * Driven by mutations, not by frames or timers. requestAnimationFrame does not tick while a
 * tab is backgrounded or occluded, which silently stranded an earlier version of this: the
 * click handler ran, but the readiness callback never fired because the frame loop never
 * advanced. A MutationObserver runs on the microtask checkpoint and is unaffected.
 *
 * The common case — the customer choosing the option the page is already on — is handled
 * synchronously before any observer is created.
 *
 * @param {string} variantId
 * @param {(button: HTMLButtonElement) => void} run
 * @param {() => void} onGiveUp
 */
function whenFormReady(variantId, run, onGiveUp) {
  /** @returns {HTMLButtonElement | null} */
  const readyButton = () => {
    const form = /** @type {HTMLFormElement | null} */ (
      document.querySelector('form[data-type="add-to-cart-form"]')
    );
    const idField = /** @type {HTMLInputElement | null} */ (form?.elements.namedItem('id') ?? null);
    const button = addToCartButton();
    const ready = form?.isConnected && idField?.value === variantId && button && !button.disabled;
    return ready ? button : null;
  };

  const immediate = readyButton();
  if (immediate) {
    run(immediate);
    return;
  }

  let settled = false;
  const finish = (/** @type {HTMLButtonElement | null} */ button) => {
    if (settled) return;
    settled = true;
    observer.disconnect();
    clearTimeout(timer);
    if (button) run(button);
    else onGiveUp();
  };

  // Only settle when the form is actually ready. Settling on the first mutation regardless
  // gave up during the re-render's intermediate states — the button is briefly disabled
  // while the theme swaps variants, which is exactly when the first mutation arrives.
  const observer = new MutationObserver(() => {
    const button = readyButton();
    if (button) finish(button);
  });
  observer.observe(document.querySelector('.product-information') ?? document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['value', 'disabled'],
  });

  // A backstop only — the observer is the mechanism, this just stops a failed re-render
  // leaving the button latched.
  const timer = setTimeout(() => finish(readyButton()), READY_TIMEOUT);
}

// One delegated listener for the life of the document. The CTA markup is re-rendered on
// variant change, so binding to the buttons themselves would leak handlers and produce
// duplicate cart lines after a few switches.
document.addEventListener('click', (event) => {
  const target = /** @type {HTMLElement | null} */ (event.target);
  const button = /** @type {HTMLButtonElement | null} */ (
    target?.closest?.('.final-cta__button') ?? null
  );
  if (!button || button.disabled) return;

  const variantId = button.dataset.variantId;
  if (!variantId) return;

  // A click already in flight must not queue a second add — one press, one cart line.
  if (button.dataset.adding === 'true') return;
  button.dataset.adding = 'true';

  const release = () => delete button.dataset.adding;

  if (!selectVariant(variantId)) {
    release();
    return;
  }

  whenFormReady(
    variantId,
    (addToCart) => {
      addToCart.click();
      release();
    },
    release
  );
});

/**
 * Hands the floating purchase bar off to the closing CTA.
 *
 * A persistent Add to cart parked on top of a full purchase module is two competing
 * checkouts in one viewport, so the bar stands down while the CTA is on screen and returns
 * as soon as it scrolls away.
 *
 * Two mechanisms drive one idempotent write. An IntersectionObserver is the primary and is
 * what a normal browser uses. A passive scroll listener is the fallback, because the
 * observer only delivers callbacks on a rendering opportunity — in a backgrounded or
 * occluded tab it goes quiet, which is the same class of failure that stranded the CTA's
 * add-to-cart on requestAnimationFrame. The listener also covers the case where the CTA's
 * height changes under it without the page scrolling.
 *
 * Both paths call `apply`, which writes only on an actual change, so the two can overlap
 * freely without flicker or redundant attribute churn.
 *
 * Generic by design: it binds to the sticky host and whatever element carries
 * `data-final-cta`, with no product, price or variant knowledge, so every PDP on this
 * template gets the same behaviour.
 */
function bindStickyHandoff() {
  const cta = document.querySelector('[data-final-cta]');
  const sticky = document.querySelector('sticky-add-to-cart');
  if (!cta || !sticky) return;

  let suppressed = null;

  /** The CTA overlaps the viewport, so the bar should stand down. */
  const shouldSuppress = () => {
    const box = cta.getBoundingClientRect();
    const viewport = window.innerHeight || document.documentElement.clientHeight;
    return box.top < viewport && box.bottom > 0;
  };

  const apply = () => {
    const next = shouldSuppress();
    if (next === suppressed) return;
    suppressed = next;
    sticky.toggleAttribute('data-suppressed', next);
  };

  new IntersectionObserver(apply, { threshold: 0 }).observe(cta);

  // The page scrolls inside a container at desktop widths and on the document below that,
  // so listen to whichever ancestor actually scrolls, plus the window.
  const scroller = cta.closest('.page-wrapper') ?? document;
  scroller.addEventListener('scroll', apply, { passive: true });
  if (scroller !== document) document.addEventListener('scroll', apply, { passive: true });
  window.addEventListener('resize', apply, { passive: true });

  apply();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bindStickyHandoff, { once: true });
} else {
  bindStickyHandoff();
}
